import React, { useContext, useEffect } from 'react';
import * as yup from 'yup';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import { InputText } from 'primereact/inputtext';
import { isNullOrUndefined } from '@utils/utils';
import useForm, { proxiedPropertiesOf } from '@hooks/useForm/useForm';
import { OpeningEntityTypeEnum } from '@api/ringil-api.v2';
import { findErrorMessage } from '@utils/validationUtils.ts';
import LocationOpeningHoursEditor, { emptyOpeningHours, openingHoursValidationSchema } from '@components/RingilComponents/LocationOpeningHours/LocationOpeningHoursEditor.tsx';
import LocationSidebarHeader from '@components/RingilComponents/LocationSidebarHeader/LocationSidebarHeader.tsx';
import { FieldLabel } from '@components/framework/formfield/FieldLabel.tsx';
import InputYesNo from '@components/ringil3/Input/InputSelect/InputYesNo.tsx';
import InputTimezone from '@components/ringil3/Input/InputTimezone/InputTimezone.tsx';
import InputTextWithSuffix from '@components/ringil3/Input/InputTextWithSuffix/InputTextWithSuffix.tsx';
import { FormData, rampRequestBody } from '@app/pages/customer/LocalityList/sidebars/addEditRampMapping.ts';

interface ComponentProps {
  visible: boolean;
  onHide: () => void;
  onCompleted: () => void;
  locationId?: string;
  editRampId?: string;
  timezoneId?: string;
}

const AddEditRamp: React.FC<ComponentProps> = ({ visible, onHide, onCompleted, locationId, timezoneId, editRampId }) => {
  const { tr } = useTranslationLgs();
  const { createRampForLocation, getRampDetailForLocation, updateRampForLocation } = useContext(ApiContext);

  const names = proxiedPropertiesOf<FormData>();
  const initialData: FormData = {
    rampName: '',
    isPrivate: false,
    openingHourDefinitions: emptyOpeningHours(OpeningEntityTypeEnum.RAMP),
    isShowOnRampDashboard: null,
    isTemporaryClosed: false,
    timezoneId: timezoneId,
  };

  useEffect(() => {
    if (visible) {
      formik.clear();
      // Set the timezone from the location when adding a new ramp
      if (isNullOrUndefined(editRampId) && timezoneId) {
        formik.setFieldValue(names.timezoneId, timezoneId);
      }
    }
    if (!isNullOrUndefined(editRampId)) {
      getRampDetailForLocation(locationId, editRampId, d => {
        formik.setFieldValue(names.rampName, d.name);
        formik.setFieldValue(names.isPrivate, d.isPrivate);
        formik.setFieldValue(names.isShowOnRampDashboard, d.isShowOnRampDashboard);
        formik.setFieldValue(names.openingHourDefinitions, d.openingHourDefinitions);
        formik.setFieldValue(names.maxReservationCount, d.maxReservationCount);
        formik.setFieldValue(names.maxSkuCount, d.maxSkuAmount);
        formik.setFieldValue(names.weeklyMaxReservationCount, d.weeklyMaxReservationCount);
        formik.setFieldValue(names.weeklyMaxSkuCount, d.weeklyMaxSkuAmount);
        formik.setFieldValue(names.isTemporaryClosed, d.isTemporaryClosed);
        formik.setFieldValue(names.timezoneId, d.timezoneId);
      });
    }
  }, [visible, timezoneId, editRampId]);

  const validationSchema = yup.object().shape({
    rampName: yup.string().required(tr('validation.required', 'Value is required')),
    openingHourDefinitions: openingHoursValidationSchema,
  });

  const formik = useForm<FormData>(validationSchema, initialData, values => save(values), false, false);

  const save = (values: FormData) => {
    const body = rampRequestBody(values);
    if (isNullOrUndefined(editRampId)) {
      createRampForLocation(locationId, body, () => onCompleted());
    } else {
      updateRampForLocation(locationId, editRampId, body, () => onCompleted());
    }
  };

  const rampNameError = findErrorMessage(formik.validationErrors, names.rampName, tr);

  return (
    <RingilSidebar
      visible={visible}
      heading={editRampId ? tr('AddEditRamp.editHeading', 'Ramp settings') : tr('AddEditRamp.addRamp', 'Add ramp')}
      onBack={onHide}
      onContinue={() => formik.validateAndSend()}
      continueLabel={tr('generic.save', 'Save')}
      dataTestIdContinue={'location-ramp-save'}
      dataTestIdBack={'location-ramp-back'}>
      <div className={'flex flex-col gap-8'}>
        <LocationSidebarHeader locationId={locationId} />

        <div className={'flex flex-col gap-4'}>
          <div className={'flex gap-2'}>
            <div className={'flex flex-1 flex-col gap-1'}>
              <FieldLabel label={tr('AddEditRamp.rampName', 'Ramp name')} helpText={tr('AddEditRamp.rampNameHelp', 'Name shown for this ramp')} />
              <InputText className={'w-full'} value={formik.values.rampName} onChange={e => formik.setFieldValue(names.rampName, e.target.value)} data-testid={'location-ramp-name'} />
              {rampNameError && <div className={'text-complementary-error text-[10px]'}>{rampNameError}</div>}
            </div>
            <div className={'flex flex-1 flex-col gap-1'}>
              <FieldLabel label={tr('generic.timezone', 'Timezone')} />
              <InputTimezone value={formik.values.timezoneId} onChange={v => formik.setFieldValue(names.timezoneId, v)} dataTestId={'location-ramp-timezone'} />
            </div>
          </div>

          <LocationOpeningHoursEditor
            value={formik.values.openingHourDefinitions}
            onChange={v => formik.setFieldValue(names.openingHourDefinitions, v)}
            validationErrors={formik.validationErrors}
            validationPrefix={'openingHourDefinitions.'}
          />

          <div className={'flex flex-col gap-4'}>
            <div className={'flex flex-col gap-1'}>
              <FieldLabel label={tr('AddEditRamp.internalRamp', 'Internal ramp')} />
              <InputYesNo value={formik.values.isPrivate} onChange={v => formik.setFieldValue(names.isPrivate, v)} dataTestId={'location-ramp-internal'} />
            </div>
            <div className={'flex flex-col gap-1'}>
              <FieldLabel label={tr('AddEditRamp.showOnInternalTv', 'Show on internal TV')} />
              <InputYesNo value={formik.values.isShowOnRampDashboard} onChange={v => formik.setFieldValue(names.isShowOnRampDashboard, v)} dataTestId={'location-ramp-showTv'} />
            </div>
            <div className={'flex flex-col gap-1'}>
              <FieldLabel label={tr('AddEditRamp.temporarilyClosed', 'Temporarily closed')} />
              <InputYesNo value={formik.values.isTemporaryClosed} onChange={v => formik.setFieldValue(names.isTemporaryClosed, v)} dataTestId={'location-ramp-tempClosed'} />
            </div>
          </div>

          <div className={'flex flex-col gap-4'}>
            <div className={'flex gap-2'}>
              <div className={'flex flex-1 flex-col gap-1'}>
                <FieldLabel label={tr('AddEditRamp.maxSkuCount', 'Handling units limit')} helpText={tr('AddEditRamp.maxSkuCountHelp', 'Max handling units per day')} />
                <InputTextWithSuffix suffix={tr('AddEditRamp.perDay', 'day')} value={formik.values.maxSkuCount ?? ''} onChange={v => formik.setFieldValue(names.maxSkuCount, v)} fullWidth dataTestId={'location-ramp-maxSku'} />
              </div>
              <div className={'flex flex-1 flex-col gap-1'}>
                <FieldLabel label={tr('AddEditRamp.maxReservationCount', 'Bookings limit')} helpText={tr('AddEditRamp.maxReservationCountHelp', 'Max bookings per day')} />
                <InputTextWithSuffix suffix={tr('AddEditRamp.perDay', 'day')} value={formik.values.maxReservationCount ?? ''} onChange={v => formik.setFieldValue(names.maxReservationCount, v)} fullWidth dataTestId={'location-ramp-maxReservation'} />
              </div>
            </div>
            <div className={'flex gap-2'}>
              <div className={'flex flex-1 flex-col gap-1'}>
                <FieldLabel label={tr('AddEditRamp.weeklyMaxSkuCount', 'Weekly handling units limit')} helpText={tr('AddEditRamp.weeklyMaxSkuCountHelp', 'Max handling units per week')} />
                <InputTextWithSuffix suffix={tr('AddEditRamp.perWeek', 'week')} value={formik.values.weeklyMaxSkuCount ?? ''} onChange={v => formik.setFieldValue(names.weeklyMaxSkuCount, v)} fullWidth dataTestId={'location-ramp-weeklyMaxSku'} />
              </div>
              <div className={'flex flex-1 flex-col gap-1'}>
                <FieldLabel label={tr('AddEditRamp.weeklyMaxReservationCount', 'Weekly bookings limit')} helpText={tr('AddEditRamp.weeklyMaxReservationCountHelp', 'Max bookings per week')} />
                <InputTextWithSuffix suffix={tr('AddEditRamp.perWeek', 'week')} value={formik.values.weeklyMaxReservationCount ?? ''} onChange={v => formik.setFieldValue(names.weeklyMaxReservationCount, v)} fullWidth dataTestId={'location-ramp-weeklyMaxReservation'} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </RingilSidebar>
  );
};

export default AddEditRamp;
